import Gooey, {
    calc,
    dict,
    field,
    mount,
    ref,
    type Component,
} from '@srhazi/gooey';
import {
    isBoolean,
    isExact,
    isRecordOf,
    isShape,
    isString,
    type AssertFnType,
} from '@srhazi/shape';

import './bingoals.css';

type CellId = {
    row: number;
    col: number;
};

const selectedCell = field<CellId | null>(null);
const moveFocus = field<string | null>(null);

// Keys are: `${row}-${col}`
// Values are the string contents of the cell
const cellContents = dict<string, string>();
const cellComplete = dict<string, boolean>();
const cardName = field('My goals');

const isSavedData = isShape({
    version: isExact(1),
    cardName: isString,
    cellData: isRecordOf(isShape({ value: isString, completed: isBoolean })),
});

function resetData() {
    for (let i = 0; i < 5; ++i) {
        for (let j = 0; j < 5; ++j) {
            const id = `${i}-${j}`;
            cellContents.set(id, id === '2-2' ? 'FREE CELL' : '');
            cellComplete.set(id, id === '2-2');
        }
    }
    cardName.set('My goals');
}
resetData();

const saveData = calc(() => {
    const toPersist: AssertFnType<typeof isSavedData> = {
        version: 1,
        cardName: cardName.get(),
        cellData: {},
    };
    for (let i = 0; i < 5; ++i) {
        for (let j = 0; j < 5; ++j) {
            const id = `${i}-${j}`;
            toPersist.cellData[id] = {
                value: cellContents.get(id) ?? '',
                completed: cellComplete.get(id) ?? false,
            };
        }
    }
    return toPersist;
});

function restoreData() {
    const stored = localStorage.getItem('app-data');
    if (!stored) {
        return;
    }
    let result: unknown;
    try {
        result = JSON.parse(stored);
    } catch {
        return;
    }
    if (!isSavedData(result)) {
        return;
    }
    cardName.set(result.cardName);
    const cellData = result.cellData;
    for (let i = 0; i < 5; ++i) {
        for (let j = 0; j < 5; ++j) {
            const id = `${i}-${j}`;
            if (cellData[id]) {
                cellContents.set(id, result.cellData[id].value);
                cellComplete.set(id, result.cellData[id].completed);
            }
        }
    }
}

const Header: Component = () => {
    return (
        <header class="Header">
            <div class="Header_row">
                <h1 class="Header_main">BINGO</h1>
                <h2 class="Header_subhead">
                    <span class="Header_left">20</span>
                    <span class="Header_right">26</span>
                </h2>
            </div>
            <div class="Header_row">
                <input
                    value={cardName}
                    on:input={(e, el) => cardName.set(el.value)}
                    class="Header_name"
                />
            </div>
        </header>
    );
};

const Cell: Component<{ row: number; col: number }> = ({ row, col }) => {
    const id = `${row}-${col}`;
    return (
        <div
            class={calc(() => {
                const classes = ['Cell'];
                const selected = selectedCell.get();
                if (selected && selected.row === row && selected.col === col) {
                    classes.push('Cell_input-selected');
                }
                return classes.join(' ');
            })}
        >
            <textarea
                value={calc(() => cellContents.get(id))}
                draggable
                on:dragstart={(e) => {
                    e.dataTransfer?.setData(
                        'text/plain',
                        cellContents.get(id) ?? ''
                    );
                    e.dataTransfer?.setData('text/x-bingoal', id);
                }}
                on:dragover={(e) => {
                    e.preventDefault();
                }}
                on:drop={(e, el) => {
                    const source = e.dataTransfer?.getData('text/x-bingoal');
                    if (!source) {
                        return;
                    }
                    e.preventDefault();
                    const sourceData = cellContents.get(source);
                    const sourceComplete = cellComplete.get(source);
                    cellContents.set(source, cellContents.get(id) ?? '');
                    cellComplete.set(source, !!cellComplete.get(id));
                    cellContents.set(id, sourceData ?? '');
                    cellComplete.set(id, !!sourceComplete);
                    selectedCell.set({ row, col });
                    el.focus();
                }}
                class="Cell_input"
                on:input={(e, el) => {
                    cellContents.set(id, el.value);
                }}
                on:focus={(e, el) => {
                    selectedCell.set({ row, col });
                }}
            />
            {calc(() =>
                cellComplete.get(id) ? <div class="Cell_complete" /> : null
            )}
        </div>
    );
};

const Board: Component = () => {
    const items = [];
    for (let i = 0; i < 5; ++i) {
        for (let j = 0; j < 5; ++j) {
            items.push(<Cell row={i} col={j} />);
        }
    }
    return <div class="Board">{items}</div>;
};

const Form: Component = (_, { onMount }) => {
    return (
        <div class="Form">
            <input
                type="checkbox"
                id="Form_doneCheck"
                class="Form_doneCheck"
                checked={calc(() => {
                    const selected = selectedCell.get();
                    if (selected === null) {
                        return false;
                    }
                    return !!cellComplete.get(
                        `${selected.row}-${selected.col}`
                    );
                })}
                on:input={(e, el) => {
                    const selected = selectedCell.get();
                    if (!selected) {
                        return;
                    }
                    cellComplete.set(
                        `${selected.row}-${selected.col}`,
                        el.checked
                    );
                }}
                disabled={calc(() => selectedCell.get() === null)}
            />
            <label class="Form_doneLabel" for="Form_doneCheck">
                Done
            </label>
            <button
                class="Form_resetCard"
                on:click={() => {
                    if (confirm('Delete all data?')) {
                        resetData();
                    }
                }}
            >
                Hard Reset
            </button>
        </div>
    );
};

const App: Component = (_, { onMount }) => {
    onMount(() => {
        restoreData();

        return saveData.subscribe((err, value) => {
            if (!err) {
                const saved = JSON.stringify(value);
                window.localStorage.setItem('app-data', saved);
            }
        });
    });
    return (
        <>
            <Header />
            <Board />
            <Form />
        </>
    );
};

const root = document.getElementById('main');
if (root) {
    mount(root, <App />);
} else {
    alert('Error: no #main element found!');
}
